import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { fetchCoaches } from '../services/coachService';
import { fetchPlayersForSession } from '../services/playerService';
import { Player } from '../types/Player';
import { Coach } from '../types/Coach';
import { AgeGroupWithRange } from '../lib/ageGroups';
import isEqual from 'lodash/isEqual'; // Using lodash for deep comparison

interface AgeGroup {
  id: string;
  group_name: string;
  sex: 'male' | 'female';
}

export interface EvaluationSession {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  location_name: string;
  evaluation_session_age_groups: Array<{
    age_group_id: string;
    age_groups: AgeGroup;
  }>;
}

// Polling interval in milliseconds (e.g., 2 minutes)
const PLAYER_POLLING_INTERVAL = 120000;

/**
 * Custom hook to fetch and manage evaluation session data, including polling for player updates.
 * @param evaluationId The ID of the evaluation session
 * @returns Object containing evaluation session data, players, coaches, and loading/error states
 */
export function useEvaluationSessionData(evaluationId: string | undefined) {
  const [evaluation, setEvaluation] = useState<EvaluationSession | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [allAgeGroups, setAllAgeGroups] = useState<AgeGroupWithRange[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const playerPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentPlayersRef = useRef<Player[]>([]); // Ref to hold current players for comparison

  // Update ref whenever players state changes
  useEffect(() => {
    currentPlayersRef.current = players;
  }, [players]);

  // Load coaches from Supabase (only once)
  useEffect(() => {
    async function loadCoaches() {
      const fetchedCoaches = await fetchCoaches();
      setCoaches(fetchedCoaches);
    }
    loadCoaches();
  }, []);

  // Load all age groups (with birth date ranges) once for proper-age-group lookup
  useEffect(() => {
    async function loadAgeGroups() {
      const { data, error: fetchError } = await supabase
        .from('age_groups')
        .select('id, group_name, sex, birth_date_min, birth_date_max');
      if (fetchError) {
        console.error('Error fetching age groups for proper-age lookup:', fetchError);
        return;
      }
      setAllAgeGroups((data || []) as AgeGroupWithRange[]);
    }
    loadAgeGroups();
  }, []);

  // Fetch the evaluation session (only when evaluationId changes)
  useEffect(() => {
    async function fetchEvaluation() {
      if (!evaluationId) {
        setEvaluation(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null); // Reset error on new fetch
      try {
        const { data, error: fetchError } = await supabase
          .from('evaluation_sessions')
          .select(`
            id,
            session_date,
            start_time,
            end_time,
            location_name,
            evaluation_session_age_groups!inner(
              age_group_id,
              age_groups(id, group_name, sex)
            )
          `)
          .eq('id', evaluationId)
          .single();

        if (fetchError) {
          setError(fetchError.message);
          setEvaluation(null);
        } else if (data) {
          // Transform data to match the TypeScript interface
          const typedData: EvaluationSession = {
            id: data.id,
            session_date: data.session_date,
            start_time: data.start_time,
            end_time: data.end_time,
            location_name: data.location_name,
            evaluation_session_age_groups: (data.evaluation_session_age_groups || []).map((group: any) => ({
              age_group_id: group.age_group_id,
              age_groups: {
                id: group.age_groups.id,
                group_name: group.age_groups.group_name,
                sex: group.age_groups.sex
              }
            }))
          };
          setEvaluation(typedData);
        } else {
            setEvaluation(null); // Handle case where no data is returned without error
            setError('Evaluation session not found.');
        }
      } catch (err) {
        console.error('Error fetching evaluation session:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        setEvaluation(null);
      } finally {
        setIsLoading(false);
      }
    }
    fetchEvaluation();
  }, [evaluationId]);

  // Fetch initial players and set up polling
  const loadPlayers = useCallback(async (isPolling = false) => {
    if (!evaluationId) {
        setPlayers([]); // Clear players if no session ID
        return;
    }

    try {
      const fetchedPlayers = await fetchPlayersForSession(evaluationId);

      // Compare fetched data with current data (using ref to avoid stale closure)
      // Focus comparison on relevant fields like evaluatedByCoachIds
      const currentMapped = currentPlayersRef.current.map(p => ({ id: p.id, evaluatedBy: p.evaluatedByCoachIds?.sort() }));
      const fetchedMapped = fetchedPlayers.map(p => ({ id: p.id, evaluatedBy: p.evaluatedByCoachIds?.sort() }));

      if (!isEqual(currentMapped, fetchedMapped)) {
        console.log(`[useEvaluationSessionData Polling] Player data changed, updating state.`);
        setPlayers(fetchedPlayers); // Update state only if data differs significantly
      } else if (isPolling) {
        // console.log(`[useEvaluationSessionData Polling] No player changes detected.`);
      }

      if (!isPolling && error) { // Clear error if initial fetch succeeds
        setError(null);
      }

    } catch (err) {
      console.error('Error fetching players for session:', err);
      // Only set error on initial load, not necessarily on polling failures unless desired
      if (!isPolling) {
         setError(err instanceof Error ? err.message : 'An error occurred while fetching players');
      }
    }
  }, [evaluationId, error]); // Include error in dependencies to potentially clear it

  useEffect(() => {
    // Clear existing interval if evaluationId changes
    if (playerPollingIntervalRef.current) {
      clearInterval(playerPollingIntervalRef.current);
      playerPollingIntervalRef.current = null;
    }

    if (evaluationId) {
      // Initial load
      loadPlayers(false);

      // Set up polling
      // Note: Consider using Supabase Realtime (subscriptions) for a more efficient approach than polling.
      playerPollingIntervalRef.current = setInterval(() => {
          console.log('[useEvaluationSessionData Polling] Fetching player updates...');
          loadPlayers(true); // Indicate this is a polling call
        }, PLAYER_POLLING_INTERVAL);

      // Cleanup function to clear interval on unmount or when evaluationId changes
      return () => {
        if (playerPollingIntervalRef.current) {
          clearInterval(playerPollingIntervalRef.current);
          playerPollingIntervalRef.current = null;
          console.log('[useEvaluationSessionData Polling] Polling stopped.');
        }
      };
    } else {
        // Ensure players are cleared if evaluationId becomes undefined
        setPlayers([]);
        currentPlayersRef.current = [];
    }
  }, [evaluationId, loadPlayers]); // Re-run when evaluationId changes or loadPlayers function definition changes

  return {
    evaluation,
    players,
    setPlayers, // Keep exposing setPlayers for direct updates (like after save)
    coaches,
    allAgeGroups,
    isLoading,
    error,
    loadPlayers // Expose loadPlayers for manual refresh
  };
}